import {
  PlainButton,
  RenderTooltipTriggerProps,
  Tooltip,
} from "@jugl-web/ui-components";
import { useTranslations } from "@jugl-web/utils";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { FC, ReactElement } from "react";
import { useTicketsLimit } from "../../hooks/useTicketsLimit";

interface TicketCreationBlockedTooltipProps {
  renderTrigger: (props: RenderTooltipTriggerProps) => ReactElement;
}

/**
 * Wraps a (disabled) ticket-creation trigger in a tooltip that explains why new
 * tickets can't be created — either the plan limit is reached or the workspace
 * subscription is canceled. The canceled variant exposes a "Manage Subscription"
 * link that navigates to the workspace plans page.
 */
export const TicketCreationBlockedTooltip: FC<
  TicketCreationBlockedTooltipProps
> = ({ renderTrigger }) => {
  const { t } = useTranslations();
  const { navigateToPage } = useNavigation();
  const { isSubscriptionCanceled, limit } = useTicketsLimit();

  return (
    <Tooltip
      placement="bottom"
      className="text-center"
      isInteractive={isSubscriptionCanceled}
      renderTrigger={renderTrigger}
    >
      {isSubscriptionCanceled
        ? t(
            {
              id: "client-support-page.tickets-subscription-canceled-tooltip",
              defaultMessage:
                "Subscription is Canceled. {manageSubscription} to Create New Tickets",
            },
            {
              manageSubscription: (
                <PlainButton
                  className="font-secondary text-sm font-medium text-white underline"
                  onClick={() => navigateToPage("cxWorkspacePlans")}
                >
                  {t({
                    id: "client-support-page.manage-subscription",
                    defaultMessage: "Manage Subscription",
                  })}
                </PlainButton>
              ),
            }
          )
        : t(
            {
              id: "client-support-page.tickets-limit-reached-tooltip",
              defaultMessage:
                "New Tickets cannot be created. You reached the limit of {limit} Tickets per your Workspace Plan 🎫",
            },
            { limit }
          )}
    </Tooltip>
  );
};
